/**
 * Sunday Sports Reviews API client.
 *
 * Mirrors the contract documented in docs/reviews-system.md. Explicit mock
 * mode is available for local development, while storefront traffic always
 * uses the production API and never substitutes fixture reviews.
 *
 * Backend host: https://app.sundaysports.com/api/v1/  (same backend that
 * serves Live TV — see assets/youtube-live-api.js for the fetch pattern).
 */

// Load guard: this file is injected by several review components (reviews-pdp,
// reviews-slideshow, write-review-form, review-stars-badge, global-review-stars-
// badge). As a plain (non-module) <script defer>, each tag re-executes in global
// scope — so multiple review components on one page (e.g. the ade-authority LP)
// made the 2nd execution throw "Identifier 'BASE_URL' has already been declared",
// aborting it and spamming the console. Wrapping the module in a guarded IIFE
// makes every execution after the first a clean no-op; consumers only ever touch
// window.SundayReviewsAPI.
(function () {
if (window.SundayReviewsAPI) return;

const BASE_URL = "https://app.sundaysports.com/api/v1/reviews";
// Fixture reviews are restricted to local development. A URL parameter or a
// storefront script must never be able to turn them on in production.
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);
const USE_MOCK = LOCAL_HOSTS.has(window.location.hostname) && window.SUNDAY_REVIEWS_MOCK === true;
const REVIEW_COUNT_SCALE = 26;

function applyReviewCountScale(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n * REVIEW_COUNT_SCALE : 0;
}

// ---- Mock fixtures -------------------------------------------------------

const MOCK_NAMES = [
  "Mark S.", "Jamie P.", "Ali R.", "Chris T.", "Sam K.", "Pat L.",
  "Jordan M.", "Riley B.", "Casey D.", "Taylor V.", "Morgan H.", "Reese A.",
  "Drew C.", "Skyler N.", "Quinn O.", "Avery W.",
];
const MOCK_TITLES = [
  "Best electrolyte mix I've tried",
  "Tastes like a real orange",
  "Game changer on long runs",
  "Better than the big brands",
  "My new daily",
  "Wish I bought sooner",
  "Hits the spot post-workout",
  "Subtle, not syrupy",
];
const MOCK_BODIES = [
  "Light, refreshing, and the orange flavor is dialed in. Doesn't have that fake powder taste at all. I've been mixing it after morning runs for two weeks and it's the only thing that's actually replaced my old habit of grabbing a sports drink at the gas station.",
  "I'm picky about sweetness and this nailed it. Not cloying, not watered down. The hit of sodium is noticeable in a good way — I can tell it's working.",
  "Brought a few packets to a tournament weekend. Held up better than I expected through three matches a day. Going to grab the variety pack next.",
  "Half a scoop is plenty if you're sensitive to sweetness. Mixes clean with no clumping in a shaker.",
  "The flavor is more 'orange peel' than 'orange candy' which is exactly what I wanted. Easy on the stomach during runs.",
];

function seededRand(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function makeMockReviews(productId, count = 24) {
  const rand = seededRand(productId || 1);
  const reviews = [];
  for (let i = 0; i < count; i++) {
    const r = Math.floor(rand() * MOCK_NAMES.length);
    const t = Math.floor(rand() * MOCK_TITLES.length);
    const b = Math.floor(rand() * MOCK_BODIES.length);
    const ratingRoll = rand();
    const rating = ratingRoll < 0.62 ? 5 : ratingRoll < 0.85 ? 4 : ratingRoll < 0.94 ? 3 : ratingRoll < 0.98 ? 2 : 1;
    const daysAgo = Math.floor(rand() * 200);
    const submittedAt = new Date(Date.now() - daysAgo * 86400000).toISOString();
    const helpful = Math.floor(rand() * 40);
    const hasMedia = rand() < 0.18;
    reviews.push({
      id: `mock-${productId}-${i}`,
      shopify_product_id: productId,
      reviewer_name: MOCK_NAMES[r],
      rating,
      title: MOCK_TITLES[t],
      body: MOCK_BODIES[b],
      submitted_at: submittedAt,
      verified_buyer: rand() < 0.7,
      helpful_count: helpful,
      media: hasMedia
        ? [{ kind: "image", url: `https://picsum.photos/seed/${productId}-${i}/600/600`, width: 600, height: 600 }]
        : [],
      merchant_reply: rand() < 0.08
        ? { body: "Thanks for the kind words — glad the orange flavor is working for you!", replied_by: "Sunday Sports", created_at: submittedAt }
        : null,
    });
  }
  return reviews;
}

function summarize(reviews) {
  const count = reviews.length;
  const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
  const histogram = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach((r) => { histogram[r.rating] += 1; });
  return {
    review_count: applyReviewCountScale(count),
    average_rating: count ? Math.round((sum / count) * 100) / 100 : 0,
    histogram,
    last_review_at: reviews[0]?.submitted_at || null,
  };
}

// ---- Public API ----------------------------------------------------------

async function realFetch(path, opts = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    signal: AbortSignal.timeout(10000),
    ...opts,
  });
  if (!res.ok) {
    let detail = "";
    try { detail = await res.text(); } catch {}
    const err = new Error(`Reviews API ${res.status}${detail ? `: ${detail}` : ""}`);
    err.status = res.status;
    err.body = detail;
    throw err;
  }
  return res.json();
}

function mockGlobalSummary() {
  return {
    review_count: applyReviewCountScale(2),
    average_rating: 5,
    histogram: { 5: 2, 4: 0, 3: 0, 2: 0, 1: 0 },
    last_review_at: null,
  };
}

async function fetchGlobalSummary() {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 120));
    return mockGlobalSummary();
  }
  const data = await realFetch(`/global/stats/`);
  if (!data) throw new Error("Reviews API returned an empty global summary");
  return { ...data, review_count: applyReviewCountScale(data.review_count) };
}

async function fetchSummary(shopifyProductId) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 120));
    return summarize(makeMockReviews(shopifyProductId));
  }
  // Backend's /products/:id/stats/ currently undercounts; derive from list.
  const data = await realFetch(`/products/${shopifyProductId}/?page_size=200`);
  return summarize(data.reviews || []);
}

async function fetchSummaryBatch(productIds) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 120));
    const all = [];
    productIds.forEach((id) => all.push(...makeMockReviews(id, 12)));
    return summarize(all);
  }
  // Backend's /batch/stats/ currently undercounts; derive from list.
  const data = await realFetch(`/batch/reviews/?ids=${productIds.join(",")}&page_size=200`);
  return summarize(data.reviews || []);
}

/**
 * @param {object} params
 * @param {number} params.productId
 * @param {number} [params.page=1]
 * @param {number} [params.perPage=5]
 * @param {"newest"|"highest"|"lowest"|"helpful"} [params.sort="newest"]
 * @param {number|null} [params.rating=null]   filter by exact star rating
 * @param {boolean} [params.withMedia=false]
 */
async function fetchReviewsBatch({ productIds, page = 1, perPage = 5, sort = "newest", rating = null, withMedia = false }) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 180));
    let list = [];
    productIds.forEach((id) => list.push(...makeMockReviews(id, 12)));
    if (rating) list = list.filter((r) => r.rating === rating);
    if (withMedia) list = list.filter((r) => r.media.length > 0);
    list.sort((a, b) => {
      if (sort === "highest") return b.rating - a.rating || new Date(b.submitted_at) - new Date(a.submitted_at);
      if (sort === "lowest") return a.rating - b.rating || new Date(b.submitted_at) - new Date(a.submitted_at);
      if (sort === "helpful") return b.helpful_count - a.helpful_count;
      if (sort === "oldest") return new Date(a.submitted_at) - new Date(b.submitted_at);
      return new Date(b.submitted_at) - new Date(a.submitted_at);
    });
    const total = list.length;
    const start = (page - 1) * perPage;
    return { reviews: list.slice(start, start + perPage), page, per_page: perPage, total, has_next: start + perPage < total };
  }
  const qs = new URLSearchParams({
    ids: productIds.join(","),
    page, page_size: perPage, sort,
    ...(rating ? { rating } : {}),
    ...(withMedia ? { with_media: "true" } : {}),
  });
  return realFetch(`/batch/reviews/?${qs}`);
}

async function fetchReviews({ productId, page = 1, perPage = 5, sort = "newest", rating = null, withMedia = false }) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 180));
    let list = makeMockReviews(productId);
    if (rating) list = list.filter((r) => r.rating === rating);
    if (withMedia) list = list.filter((r) => r.media.length > 0);
    list.sort((a, b) => {
      if (sort === "highest") return b.rating - a.rating || new Date(b.submitted_at) - new Date(a.submitted_at);
      if (sort === "lowest") return a.rating - b.rating || new Date(b.submitted_at) - new Date(a.submitted_at);
      if (sort === "helpful") return b.helpful_count - a.helpful_count;
      return new Date(b.submitted_at) - new Date(a.submitted_at);
    });
    const total = list.length;
    const start = (page - 1) * perPage;
    return {
      reviews: list.slice(start, start + perPage),
      page,
      per_page: perPage,
      total,
      has_next: start + perPage < total,
    };
  }
  const qs = new URLSearchParams({
    page, page_size: perPage, sort,
    ...(rating ? { rating } : {}),
    ...(withMedia ? { with_media: "true" } : {}),
  });
  return realFetch(`/products/${productId}/?${qs}`);
}

async function submitReview(payload) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 400));
    return { id: `mock-new-${Date.now()}`, status: "pending" };
  }
  return realFetch(`/submit/`, { method: "POST", body: JSON.stringify(payload) });
}

async function requestUploadSession() {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 100));
    return { session_id: `mock-session-${Math.random().toString(36).slice(2, 12)}`, expires_in: 7200 };
  }
  return realFetch(`/upload-session/`, { method: "POST", body: "{}" });
}

async function requestMediaUploadUrl({ token, session_id, kind, content_type, filename }) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 200));
    const ext = (filename || "").split(".").pop() || (kind === "video" ? "mp4" : "jpg");
    const id = Math.random().toString(36).slice(2, 10);
    const ns = token ? `${token}` : `unsolicited/${session_id}`;
    return {
      upload_url: `https://example.invalid/mock-upload/${id}`,
      storage_path: `${ns}/${id}.${ext}`,
      public_url: `https://example.invalid/public/${ns}/${id}.${ext}`,
      kind,
      content_type,
      expires_in: 7200,
      // The live endpoint dictates a long-lived cache policy so stored
      // objects aren't re-billed on every view. Mirror it here so the
      // upload path exercises the same forwarding in dev.
      cache_control: "public, max-age=31536000, immutable",
    };
  }
  const body = token ? { token, kind, content_type, filename } : { session_id, kind, content_type, filename };
  return realFetch(`/media/upload-url/`, { method: "POST", body: JSON.stringify(body) });
}

async function uploadMediaFile(uploadUrl, file, { cacheControl, contentType } = {}) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 300));
    return { ok: true };
  }
  const headers = { "Content-Type": contentType || file.type };
  // Forward the cache policy the API hands back (cache_control from
  // requestMediaUploadUrl). Supabase otherwise stores signed-URL uploads as
  // no-cache, so the media re-downloads on every view and re-bills egress —
  // exactly what blew the 5 GB cap. PUTting raw to the signed URL means it
  // travels as the Cache-Control request header.
  if (cacheControl) headers["Cache-Control"] = cacheControl;
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers,
    body: file,
  });
  if (!res.ok) throw new Error(`Media upload ${res.status}`);
  return { ok: true };
}

// Hand-curated mock featured reviews. Match the visual tone of the
// homepage slideshow card (short punchy title, short body, name + role/location).
const MOCK_FEATURED = [
  {
    id: "mock-feat-1",
    rating: 5,
    title: "Last night a lollipop saved my life",
    body: "My favorite dance floor party trick is to bring a bunch of electrolyte lollipops to share. On a sweaty dance floor, nothing hits like something sweet and salty.",
    reviewer_name: "Samantha",
    reviewer_role: "Minneapolis MN",
    submitted_at: new Date(Date.now() - 4 * 86400000).toISOString(),
    verified_buyer: true,
    helpful_count: 28,
    product: { id: 0, title: "Electrolyte Lollipops", url: "/products/electrolyte-lollipops" },
  },
  {
    id: "mock-feat-2",
    rating: 5,
    title: "Afternoon ritual",
    body: "Obsessed with the flavors and the super simple ingredients!! Instead of coffee number three when I hit an afternoon slump, I'll grab a Le Lick for a reset. Feels like just the right midday treat.",
    reviewer_name: "Matthew R",
    reviewer_role: "Houston TX",
    submitted_at: new Date(Date.now() - 10 * 86400000).toISOString(),
    verified_buyer: true,
    helpful_count: 42,
    product: { id: 0, title: "Le Lick", url: "/products/le-lick" },
  },
  {
    id: "mock-feat-3",
    rating: 5,
    title: "Replenish and refresh",
    body: "It's exactly what my body needs after sweating in core yoga.",
    reviewer_name: "Samantha",
    reviewer_role: "Hot yoga instructor",
    submitted_at: new Date(Date.now() - 18 * 86400000).toISOString(),
    verified_buyer: true,
    helpful_count: 15,
    product: { id: 0, title: "Orange Slice Ade", url: "/products/electrolyte-drink-mix-orange-slice-ade" },
  },
  {
    id: "mock-feat-4",
    rating: 5,
    title: "The only mix I'll buy",
    body: "Light, citrusy, and not too sweet. Replaced three different sports drinks in my fridge with a single tub.",
    reviewer_name: "Jordan M",
    reviewer_role: "Brooklyn NY",
    submitted_at: new Date(Date.now() - 25 * 86400000).toISOString(),
    verified_buyer: true,
    helpful_count: 31,
    product: { id: 0, title: "Orange Slice Ade", url: "/products/electrolyte-drink-mix-orange-slice-ade" },
  },
  {
    id: "mock-feat-5",
    rating: 5,
    title: "Long-run essential",
    body: "Brought packets on a half-marathon training block. No GI issues, real flavor, and the sodium hit kicks in fast.",
    reviewer_name: "Riley B",
    reviewer_role: "Marathoner",
    submitted_at: new Date(Date.now() - 32 * 86400000).toISOString(),
    verified_buyer: true,
    helpful_count: 19,
    product: { id: 0, title: "Variety Pack", url: "/products/variety-pack" },
  },
  {
    id: "mock-feat-6",
    rating: 5,
    title: "Tastes like the real thing",
    body: "More orange peel than orange candy — exactly what I wanted. Easy on the stomach during runs and post-workout.",
    reviewer_name: "Avery W",
    reviewer_role: "Austin TX",
    submitted_at: new Date(Date.now() - 47 * 86400000).toISOString(),
    verified_buyer: true,
    helpful_count: 24,
    product: { id: 0, title: "Orange Slice Ade", url: "/products/electrolyte-drink-mix-orange-slice-ade" },
  },
];

async function fetchFeatured({ limit = 12 } = {}) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 140));
    return { reviews: MOCK_FEATURED.slice(0, limit) };
  }
  try {
    return await realFetch(`/featured/?limit=${limit}`);
  } catch {
    return { reviews: [] };
  }
}

async function voteHelpful(reviewId) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 120));
    return { ok: true };
  }
  return realFetch(`/${reviewId}/helpful/`, { method: "POST" });
}

window.SundayReviewsAPI = {
  fetchGlobalSummary,
  fetchSummary,
  fetchSummaryBatch,
  fetchReviews,
  fetchReviewsBatch,
  fetchFeatured,
  submitReview,
  voteHelpful,
  requestUploadSession,
  requestMediaUploadUrl,
  uploadMediaFile,
  __mockEnabled: USE_MOCK,
};
})();
